package fyi.moko.hotpl

import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.window.ComposeViewport
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.FirebaseOptions
import dev.gitlive.firebase.apps
import dev.gitlive.firebase.initialize
import fyi.moko.hotpl.di.initKoin
import fyi.moko.hotpl.ui.HotplApp
import fyi.moko.hotpl.ui.theme.HotplTheme

/**
 * JS/Compose-web entry point. Mounts the shared `HotplApp` into the
 * `#ComposeTarget` container in `web/index.html` via `ComposeViewport` (the
 * Skiko canvas-based renderer — the successor to `CanvasBasedWindow` in
 * Compose 1.11). Mirrors the iOS boot in `MainViewController` and the Android
 * boot in `MainActivity`: start Koin once, then render `HotplTheme { HotplApp() }`.
 *
 * The JS bundle is produced by `:shared:jsBrowserProductionWebpack` and copied
 * into `web/` by the `:shared:copyWebApp` task; `web/index.html` loads it as
 * `shared.js`. For a hot-reload dev server use `./gradlew
 * :shared:jsBrowserDevelopmentRun`.
 */
@OptIn(ExperimentalComposeUiApi::class)
fun main() {
    initFirebase()
    initKoin()
    ComposeViewport(viewportContainerId = "ComposeTarget") {
        HotplTheme {
            HotplApp()
        }
    }
}

/**
 * Initialize the default Firebase JS app before Koin builds the graph.
 *
 * Unlike Android/iOS, the GitLive JS SDK has no `google-services.json`
 * equivalent, so nothing creates the default `[DEFAULT]` app implicitly. Because
 * `FirebaseAuthService` touches `Firebase.auth` in a property initializer, an
 * uninitialized app makes the `AuthService` singleton throw *synchronously*
 * during `initKoin()` — which propagates out of `HomeViewModel` construction and
 * crashes the whole composition to a blank canvas (not caught by
 * `SyncCoordinator`'s coroutine try/catch, which only guards the async sync).
 *
 * ── 이 값들은 비밀이 아니다 ──────────────────────────────
 * Firebase 웹 config는 **공개 식별자**다. 웹 앱이 Firebase에 접속하려면 브라우저로 내려가야
 * 하므로 숨길 방법이 원래 없고, Google도 문서에서 그렇게 명시한다. 보안은 이 값이 아니라
 * `firestore.rules.prod`가 담당한다. `local.properties`로 빼야 하는 Places/Maps 키(CLAUDE.md
 * Secrets 규칙)와는 다른 부류다 — 그쪽은 노출되면 실제로 과금이 발생한다.
 */
private fun initFirebase() {
    if (Firebase.apps().isEmpty()) {
        // 전용 Firebase **Web** 앱(`Hotpla Web`)의 값이다. 예전에는 Android 앱의 app id와
        // API 키를 빌려 썼는데, 웹 앱이 등록된 적이 없어서였다. 그 재사용은 두 가지가 문제였다:
        // Android 키가 공개 사이트에 실려 나갔고(그 키가 프로젝트의 다른 API로 무엇을 할 수
        // 있는지는 별개 문제다), authDomain이 없어 Google 팝업 로그인이 온전하지 않았다.
        Firebase.initialize(
            options = FirebaseOptions(
                applicationId = "1:597866223762:web:7388e4fcce2dafcb70ae4b",
                apiKey = "AIzaSyAtRN5oFKHohDPtrpErx5ZSN4okon8vyRw",
                projectId = "hotpla-app",
                gcmSenderId = "597866223762",
                // Cloud Storage bucket for photo bytes — same reason as HotplApplication (Android).
                storageBucket = "hotpla-app.firebasestorage.app",
                // 팝업 로그인의 리디렉션 대상. 이게 없어서 웹 소셜 로그인이 절름발이였다.
                authDomain = "hotpla-app.firebaseapp.com",
            ),
        )
    }
}